import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { readState } from "@/__main__/app-state.mjs";
import { IS_TESTING } from "@/__main__/constants.mjs";
import {
  ACTIVE_GAMES,
  GAME_PREFERRED_NAME_MAP,
  GAME_SHORT_NAMES,
  hardCodeURLs,
  LEGAL_PRIVACY_POLICY_HREF,
  LEGAL_TOS_HREF,
  LOCALES,
} from "@/app/constants.mjs";
import DownloadButtonWrapper from "@/app/DownloadButtonWrapper.jsx";
import BlitzLogo from "@/inline-assets/blitz-logo.svg";
import ArrowDownIcon from "@/inline-assets/stock-arrow-down.svg";
import BlitzWordmark from "@/inline-assets/wordmark.svg";
import { PUBLISHER_ICONS } from "@/marketing/constants/publishers.mjs";
import refs from "@/marketing/footer.refs.mjs";
import {
  ButtonContainer,
  cssDownloadButton,
  Footer,
  FooterContent,
  FooterInfo,
  FooterSub,
  LinkGroup,
  LogoContainer,
} from "@/marketing/MarketingFooter.style.jsx";
import { useShouldShowAppNavigation } from "@/util/app-route.mjs";
import { classNames } from "@/util/class-names.mjs";
import ClientOnly from "@/util/ClientOnly.jsx";
import { useSnapshot } from "@/util/use-snapshot.mjs";

const REGIONS: Array<Translation> = [
  ["home:footer.lotsOfLoveAndTiltIn.region.losAngeles", "Los Angeles, CA"],
  ["home:footer.lotsOfLoveAndTiltIn.region.turkey", "Turkey"],
  ["home:footer.lotsOfLoveAndTiltIn.region.brazil", "Brazil"],
  ["home:footer.lotsOfLoveAndTiltIn.region.lasLan", "LAS & LAN"],
  ["home:footer.lotsOfLoveAndTiltIn.region.euNordic", "EU Nordic"],
  ["home:footer.lotsOfLoveAndTiltIn.region.indonesia", "Indonesia"],
  ["home:footer.lotsOfLoveAndTiltIn.region.thailand", "Thailand"],
  ["home:footer.lotsOfLoveAndTiltIn.region.china", "China"],
  ["home:footer.lotsOfLoveAndTiltIn.region.oceania", "Oceania"],
  ["home:footer.lotsOfLoveAndTiltIn.region.russia", "Russia"],
  ["home:footer.lotsOfLoveAndTiltIn.region.japan", "Japan"],
  ["home:footer.lotsOfLoveAndTiltIn.region.euWest", "EU West"],
  ["home:footer.lotsOfLoveAndTiltIn.region.singapore", "Singapore"],
  ["home:footer.lotsOfLoveAndTiltIn.region.vietnam", "Vietnam"],
];

function RegionSpinner() {
  const [spinnerIdx, setSpinnerIdx] = useState(0);

  useEffect(() => {
    // Timer messes with testing...
    if (IS_TESTING) return;

    const timeout = setTimeout(() => {
      setSpinnerIdx((spinnerIdx + 1) % REGIONS.length);
    }, 2500);

    return () => {
      clearTimeout(timeout);
    };
  }, [spinnerIdx]);

  const { t } = useTranslation();

  return (
    <ClientOnly>
      {t(
        "home:footer.lotsOfLoveAndTiltIn.full",
        "Made with Lots of Love and Tilt in {{region}}",
        {
          region: t(...REGIONS[spinnerIdx]),
        },
      )}
    </ClientOnly>
  );
}

function getEmojiFlag(locale: string) {
  switch (locale) {
    // case "en":
    //   return ":flag-us:";
    // case "es":
    //   return ":flag-es:";
    // case "ru":
    //   return ":flag-ru:";
    // case "de":
    //   return ":flag-de:";
    // case "fr":
    //   return ":flag-fr:";
    // case "it":
    //   return ":flag-it:";
    // case "nl":
    //   return ":flag-nl:";
    // case "pt":
    //   return ":flag-pt:";
    // case "pl":
    //   return ":flag-pl:";
    // case "tr":
    //   return ":flag-tr:";
    // case "cs":
    //   return ":flag-cz:";
    // case "el":
    //   return ":flag-gr:";
    // case "ko":
    //   return ":flag-kr:";
    // case "ja":
    //   return ":flag-jp:";
    // case "vi":
    //   return ":flag-vn:";
    // case "zh-Hans-CN":
    //   return ":flag-cn:";
    // case "zh-Hant-TW":
    //   return ":flag-tw:";
    default:
      return "";
  }
}

function CurrentLanguage({ target }: { target?: string }) {
  const {
    settings: { selectedLanguage },
  } = useSnapshot(readState);

  const currentLocale = LOCALES.find(
    (lng) => Array.isArray(lng) && lng[0] === selectedLanguage,
  ) || ["en", "English"];

  const emojiFlagStr = getEmojiFlag(currentLocale[0]);

  return (
    <a href="/settings" target={target}>
      {emojiFlagStr}
      {currentLocale[1]}
    </a>
  );
}

type MarketingFooterProps = React.ComponentPropsWithoutRef<"footer"> & {
  expanded?: boolean;
  lift?: boolean;
};

export default function MarketingFooter({
  expanded,
  lift,
  ...props
}: MarketingFooterProps) {
  const shouldShowAppNavigation = useShouldShowAppNavigation();

  const standaloneTarget = shouldShowAppNavigation ? "_blank" : undefined;
  const appTarget = !shouldShowAppNavigation ? "_blank" : undefined;

  const { t } = useTranslation();

  const SOCIAL_LINKS = [
    {
      href: hardCodeURLs.BLITZ_FACEBOOK,
      label: t("common:facebook", "Facebook"),
      target: standaloneTarget,
    },
    {
      href: hardCodeURLs.BLITZ_TWITTER,
      label: t("common:twitter", "Twitter"),
      target: standaloneTarget,
    },
    {
      href: hardCodeURLs.BLITZ_INSTAGRAM,
      label: t("common:instagram", "Instagram"),
      target: standaloneTarget,
    },
    {
      href: hardCodeURLs.BLITZ_TIKTOK,
      label: t("home:footer.tiktok", "TikTok"),
      target: standaloneTarget,
    },
    {
      href: hardCodeURLs.BLITZ_DISCORD,
      label: t("common:discord", "Our Discord"),
      // Always open new tab bc our router tries to be helpful and nav to `/discord` which doesn't exist
      target: "_blank",
    },
  ];

  const COMPANY_LINKS = [
    {
      href: "/about-us",
      label: t("home:footer.aboutUs", "About Us"),
      target: standaloneTarget,
    },
    {
      href: "/partnerships",
      label: t("home:footer.partnerships", "Partnerships"),
      target: standaloneTarget,
    },
    {
      href: "/careers",
      label: t("home:footer.careers", "Careers"),
      target: standaloneTarget,
    },
    {
      href: "/brand-assets",
      label: t("home:footer.branding", "Branding"),
      target: standaloneTarget,
    },
    {
      href: hardCodeURLs.BLITZ_SUPPORT,
      label: t("common:general.contactSupport", "Contact Support"),
      target: standaloneTarget,
    },
  ];

  return (
    <Footer $lift={lift} {...props}>
      <FooterContent $expanded={expanded}>
        <FooterInfo>
          <LogoContainer>
            {refs.Logo ? (
              <refs.Logo />
            ) : (
              <>
                <BlitzLogo />
                <BlitzWordmark />
              </>
            )}
          </LogoContainer>

          <ButtonContainer>
            <div>
              <DownloadButtonWrapper
                {...classNames(cssDownloadButton(), "desktop-only")}
                size="large"
                emphasis="high"
                uiLocation="MARKETING_FOOTER"
                target="_blank"
              />

              <div className="compliance-block">
                <p className="type-caption--semi">
                  {t("welcome:header.compliant", "Fully Compliant")}
                </p>

                <ul>
                  {PUBLISHER_ICONS.map((Icon, index) => (
                    <li key={index}>
                      <Icon />
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </ButtonContainer>
        </FooterInfo>

        <LinkGroup $area="company">
          <label>
            <input type="checkbox" className="visually-hidden" />

            <div>
              <div className="title">
                {t("home:footer.company", "Company")}

                <ArrowDownIcon className="indicator" />
              </div>

              <ul>
                {(refs.Company || COMPANY_LINKS).map(
                  ({ href, label, target }) => (
                    <li key={href}>
                      <a href={href} target={target}>
                        {label}
                      </a>
                    </li>
                  ),
                )}
              </ul>
            </div>
          </label>
        </LinkGroup>

        <LinkGroup $area="socials">
          <label>
            <input type="checkbox" className="visually-hidden" />

            <div>
              <div className="title">
                {t("home:footer.socials", "Socials")}

                <ArrowDownIcon className="indicator" />
              </div>

              <ul>
                {(refs.Social || SOCIAL_LINKS).map(
                  ({ href, label, target }) => (
                    <li key={href}>
                      <a href={href} target={target}>
                        {label}
                      </a>
                    </li>
                  ),
                )}
              </ul>
            </div>
          </label>
        </LinkGroup>

        {ACTIVE_GAMES.length > 1 ? (
          <LinkGroup $area="games">
            <label>
              <input type="checkbox" className="visually-hidden" />

              <div>
                <div className="title">
                  {t("common:supportedGames", "Supported Games")}

                  <ArrowDownIcon className="indicator" />
                </div>

                <ul>
                  {ACTIVE_GAMES.map((game) => (
                    <li key={GAME_SHORT_NAMES[game]}>
                      <a href={`/${GAME_SHORT_NAMES[game]}`} target={appTarget}>
                        {t(...GAME_PREFERRED_NAME_MAP[game])}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </label>
          </LinkGroup>
        ) : null}

        <FooterSub $area="copy">
          <RegionSpinner />
        </FooterSub>

        <FooterSub $area="language">
          <CurrentLanguage target={appTarget} />
        </FooterSub>

        <FooterSub $area="terms">
          <a href={LEGAL_TOS_HREF} target={appTarget}>
            {t("home:footer.tos", "Terms of Service")}
          </a>
        </FooterSub>

        <FooterSub $area="privacy">
          <a href={LEGAL_PRIVACY_POLICY_HREF} target={appTarget}>
            {t("home:footer.pp", "Privacy Policy")}
          </a>
        </FooterSub>
      </FooterContent>
    </Footer>
  );
}
